/* 3D 파이프라인 제어기. 2D 제어기(pipeline.ts)와 같은 호스트 규약이다: 엔진이 모델에게 물을 때마다 요청을 세워 두고 호스트 모델의 응답을 기다린다.
 * 단계 캡처는 끝 카메라로 고정한 뒤 세운 호출을 앞에서부터 다시 세워 찍는다(재생은 멱등이다) */
import { jsonBody } from './jsonbody';
import type { ModelPort } from './model';
import type { Meta } from './run';
import { runDemo3d } from './run3d';
import type { Hooks3 } from './run3d';
import { clearScene3d, fitCamera3d, snapshotPng3d } from './style3d';
import type { Math3DView } from './style3d';
import { replayCalls3d } from './tools/contract3d';
import type { ToolCall } from './tools/types';
import type { ViewFit } from './figureview3d';
import type { FrameShot, HostAsk, PipelineState, StageShot, StepShot } from './pipeline';

const LOG_LIMIT = 400;
const VIEW_FILL = 0.5;

export function createPipeline3d(app: Math3DView) {
  let seq = 0;
  let pending: { ask: HostAsk; resolve: (content: string) => void; reject: (error: Error) => void } | null = null;
  let outcome: PipelineState | null = null;
  let running: AbortController | null = null;
  let waiters: (() => void)[] = [];
  const wake = (): void => { const list = waiters; waiters = []; for (const w of list) w(); };

  const port: ModelPort = (messages, options) => new Promise((resolve, reject) => {
    seq += 1;
    const ask: HostAsk = { id: seq, label: options.label ?? '', kind: options.format ? 'json' : 'text', messages, schema: options.format ?? null };
    pending = { ask, resolve: (content) => resolve({ role: 'assistant', content, thinking: '', doneReason: 'stop' }), reject };
    options.signal.addEventListener('abort', () => reject(new Error('멈췄습니다.')), { once: true });
    wake();
  });

  const cancel = (): void => { running?.abort(); running = null; pending = null; outcome = null; wake(); };

  const settle = (ms: number): Promise<void> => new Promise<void>((done) => { setTimeout(done, ms); });

  const launch = (problem: string, variantElements: string[] | undefined, controller: AbortController): void => {
    const log: string[] = [];
    let meta: Partial<Meta> = {};
    let solution = '';
    let executed: ToolCall[] = [];
    let view: ViewFit | null = null;
    const stageMarks: { label: string; count: number }[] = [];
    const stepMarks: { step: number; label: string; names: string[]; count: number; at: number }[] = [];
    const frameMarks: { label: string; names: string[]; count: number; at: number }[] = [];
    const startedAt = performance.now();
    const hooks: Hooks3 = {
      push: (kind, text) => { log.push(`[${kind}] ${text}`); if (log.length > LOG_LIMIT) log.shift(); },
      replaceThinking: () => {},
      setStatus: () => {},
      setSolution: (markdown) => { solution = markdown; },
      setMeta: (part) => { meta = { ...meta, ...part }; },
      recordCalls: (calls) => { executed = [...calls]; },
      setView: (fit) => { view = fit; },
      afterExecute: async (label) => { stageMarks.push({ label, count: executed.length }); },
      markStep: (step, label, names, count) => { stepMarks.push({ step, label, names, count, at: Math.round(performance.now() - startedAt) }); },
      markFrame: (names, label, count) => { frameMarks.push({ label, names: [...names], count, at: Math.round(performance.now() - startedAt) }); },
      latestShot: () => snapshotPng3d().png,
      model: port,
    };
    outcome = null;
    runDemo3d(app, problem, controller.signal, hooks, variantElements ? { elements: variantElements } : undefined)
      .then(async (stamp) => {
        if (running !== controller) return;
        const stages: StageShot[] = []; const stepShots: StepShot[] = []; const frames: FrameShot[] = [];
        let blank = false;
        try {
          fitCamera3d(app, VIEW_FILL, view ?? undefined);
          const camera = app.getCameraData();
          const shoot = async (count: number): Promise<string> => {
            await replayCalls3d(app, executed, count);
            if (camera) app.setCameraData(camera);
            await settle(400);
            const shot = snapshotPng3d();
            if (shot.blank) blank = true;
            return shot.png;
          };
          for (const m of stageMarks) { const png = await shoot(m.count); if (png) stages.push({ label: m.label, png }); }
          for (const m of stepMarks) { const png = await shoot(Math.min(m.count, executed.length)); if (png) stepShots.push({ step: m.step, label: m.label, names: m.names, png, at: m.at }); }
          // 호출마다 찍으면 3D 는 호출이 많아(정육면체 24개) 내보내기 상한을 넘는다. 풀이 단계가 끝나는 순간과 마지막만 프레임으로 둔다
          const keep = new Set([...stepMarks.map((m) => m.count), executed.length]);
          for (const m of frameMarks.filter((f) => keep.has(f.count))) { const png = await shoot(Math.min(m.count, executed.length)); if (png) frames.push({ label: m.label, names: m.names, png, at: m.at }); }
          await replayCalls3d(app, executed, executed.length);
          if (camera) app.setCameraData(camera);
          await settle(400);
        } catch (error) { log.push(`[error] 단계 다시 찍기 실패: ${error instanceof Error ? error.message : String(error)}`); }
        if (blank) log.push('[error] 그림이 비어 있다(WebGL 컨텍스트 손실 등). 이 환경에서는 3D 그림을 찍지 못했다. 판정과 잰 값은 장면의 좌표에서 구했으므로 유효하다');
        const times = Array.isArray(meta.timeline) ? meta.timeline : [];
        const withTimes = stages.map((st) => ({ ...st, caption: '', at: times.find((t) => t.label === st.label)?.at }));
        if (running === controller) { outcome = { state: 'done', stamp, meta, solution, log, stages: withTimes, stepShots: stepShots.sort((a, b) => a.step - b.step), frames }; running = null; wake(); }
      })
      .catch((error: unknown) => { if (running === controller) { outcome = { state: 'error', message: error instanceof Error ? error.message : String(error), log }; running = null; wake(); } });
  };

  return {
    start(problem: string, variantElements?: string[]): void {
      cancel();
      const controller = new AbortController();
      running = controller;
      outcome = null;
      void clearScene3d(app).then(() => { if (running === controller) launch(problem, variantElements, controller); })
        .catch((error: unknown) => { if (running === controller) { outcome = { state: 'error', message: `장면 비우기 실패: ${error instanceof Error ? error.message : String(error)}`, log: [] }; running = null; wake(); } });
    },

    async poll(timeoutMs = 60000): Promise<PipelineState> {
      const ready = (): PipelineState | null => (pending ? { state: 'ask', ...pending.ask } : outcome);
      const now = ready();
      if (now) return now;
      if (!running) return { state: 'idle' };
      await new Promise<void>((done) => { const timer = setTimeout(done, timeoutMs); waiters.push(() => { clearTimeout(timer); done(); }); });
      return ready() ?? (running ? { state: 'wait' } : { state: 'idle' });
    },

    submit(id: number, output: unknown): { ok: boolean; error?: string } {
      if (!pending) return { ok: false, error: '지금 응답을 기다리는 요청이 없다. pipeline_start 로 시작하거나 다음 요청을 받는다' };
      if (pending.ask.id !== id) return { ok: false, error: `기다리는 요청은 ${pending.ask.id} 번이다(받은 ${id} 번)` };
      const text = typeof output === 'string' ? output : JSON.stringify(output);
      const { ask, resolve } = pending;
      pending = null;
      resolve(ask.kind === 'json' ? jsonBody(text) : text);
      return { ok: true };
    },

    cancel,
  };
}
